import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface AssistantRow {
  readonly aiAssistantId: string;
  readonly assistantCode: string;
  readonly assistantName: string;
  readonly assistantType: string;
  readonly modelId: string | null;
  readonly isAdvisory: boolean;
  readonly requiresApproval: boolean;
  readonly status: string;
}

const ASSISTANT_TYPES = [
  'ADVERTISEMENT', 'BRANDING', 'CAMPAIGN', 'PROCUREMENT', 'VENDOR', 'COMPLIANCE',
  'ANALYTICS', 'EXECUTIVE', 'KNOWLEDGE', 'HELPDESK',
];
const STATUSES = ['DRAFT', 'ACTIVE', 'SUSPENDED', 'RETIRED'];
const PAGE = 25;

/**
 * M38 — AI Assistant Dashboard (§7). Register of the role-based enterprise AI
 * assistants (T505) across the ten governed roles. Every assistant is advisory
 * only (BR-01) — it recommends, it never acts. Authors configure an assistant
 * (ASSISTANT_MANAGE), optionally binding a governance-APPROVED model, and set
 * whether its outputs require human approval (BR-02). Filterable by type and
 * status. Tenant isolation is enforced server-side (LAW 4). A 403 surfaces a
 * clear permission message.
 */
export function AiAssistantDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<AssistantRow[]>([]);
  const [total, setTotal] = useState(0);
  const [assistantType, setAssistantType] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [name, setName] = useState('');
  const [newType, setNewType] = useState(ASSISTANT_TYPES[0]);
  const [modelId, setModelId] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [newStatus, setNewStatus] = useState('DRAFT');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<AssistantRow | null>(null);

  // §7 — change lifecycle status / remove an assistant (ASSISTANT_MANAGE).
  const [rowError, setRowError] = useState<string | null>(null);
  const [rowNotice, setRowNotice] = useState<string | null>(null);
  const [busyRowId, setBusyRowId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editStatus, setEditStatus] = useState('DRAFT');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (assistantType) params.set('assistantType', assistantType);
    if (status) params.set('status', status);
    params.set('limit', String(PAGE));
    params.set('offset', String(page * PAGE));
    apiRequest<{ data: AssistantRow[]; total: number }>(`/v1/ai/assistants?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, assistantType, status, page, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!name.trim()) { setActionError(t('m38.assistant.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: AssistantRow }>(`/v1/ai/assistants`, {
        method: 'POST',
        body: {
          assistantName: name.trim(),
          assistantType: newType,
          modelId: modelId.trim() || undefined,
          requiresApproval,
          status: newStatus,
        },
        token,
      });
      setCreated(r.data);
      setName('');
      setModelId('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m38.assistant.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const permMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? t('m38.assistant.notAuthorised')
      : (err as Error).message;

  const startEdit = (a: AssistantRow): void => {
    setRowError(null);
    setRowNotice(null);
    setEditId(a.aiAssistantId);
    setEditStatus(a.status);
  };

  const saveStatus = async (id: string): Promise<void> => {
    setRowError(null);
    setRowNotice(null);
    setBusyRowId(id);
    try {
      await apiRequest(`/v1/ai/assistants/${id}`, { method: 'PUT', body: { status: editStatus }, token });
      setEditId(null);
      setRowNotice(t('m38.assistant.updated'));
      setReload((n) => n + 1);
    } catch (err) {
      setRowError(permMessage(err));
    } finally {
      setBusyRowId(null);
    }
  };

  const removeAssistant = async (id: string): Promise<void> => {
    setRowError(null);
    setRowNotice(null);
    setBusyRowId(id);
    try {
      await apiRequest(`/v1/ai/assistants/${id}`, { method: 'DELETE', token });
      setEditId(null);
      setRowNotice(t('m38.assistant.removed'));
      setReload((n) => n + 1);
    } catch (err) {
      setRowError(permMessage(err));
    } finally {
      setBusyRowId(null);
    }
  };

  return (
    <main aria-label="ai-assistant-dashboard">
      <h1>{t('m38.assistant.title')}</h1>

      <section aria-label="create-assistant">
        <h2>{t('m38.assistant.configureHeading')}</h2>
        <form aria-label="assistant-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m38.common.name')}<input aria-label="field-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <label>{t('m38.assistant.roleType')}
            <select aria-label="field-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {ASSISTANT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m38.assistant.modelId')}<input aria-label="field-model" value={modelId} onChange={(e) => setModelId(e.target.value)} placeholder={t('m38.assistant.modelPh')} /></label>
          <label>{t('m38.assistant.requiresApproval')}
            <input aria-label="field-requires-approval" type="checkbox" checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} />
          </label>
          <label>{t('m38.common.status')}
            <select aria-label="field-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-assistant" disabled={busy}>{busy ? t('m38.common.saving') : t('m38.assistant.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="assistant-error">{actionError}</p>}
        {created && <p aria-label="assistant-created">{t('m38.assistant.created', { code: created.assistantCode })}</p>}
      </section>

      <section aria-label="assistant-filters">
        <label>{t('m38.common.type')}
          <select aria-label="filter-type" value={assistantType} onChange={(e) => { setAssistantType(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allTypes')}</option>
            {ASSISTANT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m38.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}>
            <option value="">{t('m38.common.allStatuses')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="assistant-total">{t('m38.assistant.total', { count: total })}</p>
          {rowNotice && <p aria-label="assistant-row-notice">{rowNotice}</p>}
          {rowError && <p role="alert" aria-label="assistant-row-error">{rowError}</p>}
          <DataTable<AssistantRow>
            ariaLabel="assistant-table"
            testId="assistant-table"
            rowKey={(a) => a.aiAssistantId}
            rowDataAttrs={(a) => ({ 'data-assistant': a.assistantCode })}
            emptyMessage={t('m38.assistant.noneMatch')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m38.common.code'), render: (a) => a.assistantCode },
              { key: 'name', header: t('m38.common.name'), render: (a) => a.assistantName },
              { key: 'type', header: t('m38.common.type'), render: (a) => a.assistantType },
              { key: 'advisory', header: t('m38.assistant.colAdvisory'), cellAriaLabel: (a) => `advisory-${a.assistantCode}`, render: (a) => a.isAdvisory ? t('common.yes') : t('common.no') },
              { key: 'approval', header: t('m38.assistant.colApproval'), render: (a) => a.requiresApproval ? t('m38.assistant.required') : t('m38.assistant.notRequired') },
              { key: 'status', header: t('m38.common.status'), render: (a) => a.status },
              { key: 'actions', header: t('m38.common.actions'), render: (a) => (
                editId === a.aiAssistantId ? (
                  <>
                    <select aria-label={`edit-status-${a.assistantCode}`} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <Button variant="primary" type="button" aria-label={`save-status-${a.assistantCode}`} disabled={busyRowId === a.aiAssistantId} onClick={() => void saveStatus(a.aiAssistantId)}>{t('common.save')}</Button>
                    <Button variant="danger" type="button" aria-label={`cancel-status-${a.assistantCode}`} onClick={() => setEditId(null)}>{t('common.cancel')}</Button>
                  </>
                ) : (
                  <>
                    <Button variant="secondary" type="button" aria-label={`edit-${a.assistantCode}`} onClick={() => startEdit(a)}>{t('m38.assistant.editStatus')}</Button>
                    <Button variant="danger" type="button" aria-label={`delete-${a.assistantCode}`} disabled={busyRowId === a.aiAssistantId} onClick={() => void removeAssistant(a.aiAssistantId)}>{t('common.delete')}</Button>
                  </>
                )
              ) },
            ]}
          />

          <nav aria-label="assistant-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m38.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m38.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m38.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
